import React, { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSwr from '@hooks/useSwr';
import SessionContext from '@providers/Session/SessionContext';
import { bytesFormat, getDateToBRPattern } from '@utils/formatters';
import { Badge, Button, Col, Radio, Row, Tabs, Tooltip } from 'antd';
import BlockStatusTag from '@components/BlockStatusTag';
import {
  CopyOutlined,
  DisconnectOutlined,
  ExpandAltOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import DeviceActionModal from '@components/DeviceActionModal';
import ActionTypes from '@utils/deviceActions';
import RoleChecker from '@components/RoleChecker';
import rolesConstants from '@utils/rolesConstants';
import CsvExport from '@components/CsvExport';
import Table from '@components/Table';
import { IDevice } from '@pages/Assets/Inventory/types';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';
import {
  ApplicationsConstants,
  ProductsConstants,
} from '@utils/applicationsConstants';
import { ConsumptionContainer } from './styles';
import SharedContracts from './SharedContracts';
import ConsumptionAlerts from './ConsumptionAlerts';
import ConsumptionAnalysisReport from './ConsumptionAnalysisReport';

const Consumption = () => {
  const { t } = useTranslation(['dashboard', 'reports', 'inventory', 'status']);
  const {
    isRoot,
    selectedApp,
    isMot,
    isMotBusiness,
    hasPermission,
    isMotPay,
    theme,
    hasMotLorawan,
    isMotEssential,
    isMotEnterprise,
    isNotChildOfRoot,
  } = useContext(SessionContext);

  const [modalDeviceActionsVisible, setModalDeviceActionsVisible] =
    useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<IDevice[]>();
  const [limit, setLimit] = useState('10');
  const [timeBase, setTimeBase] = useState('DAY');
  const [showContracted, setShowContracted] = useState('false');

  const showContractDash = isMot && !isRoot && showContracted === 'false';

  const showConsumptionAlerts =
    !isRoot &&
    [ProductsConstants.MULTI_CONNECT, ProductsConstants.CONNECT_ONE].includes(
      selectedApp as ApplicationsConstants,
    );

  const navigate = useNavigate();

  const { keycloak } = useContext(KeycloakContext);

  const customerTypeLogged = keycloak.idTokenParsed?.customerType;
  const isEndCustomer = customerTypeLogged === 'CUSTOMER';
  const isShowContractedTrue = isMotEssential || isMotPay || isEndCustomer;

  const routeToFindData = isMot
    ? 'MOT'
    : selectedApp?.split('_').join('').toUpperCase();

  const { data, error } = useSwr(
    `/service-proxy/inventory/${routeToFindData}/dashboards/top-consumers`,
    {
      params: { limit, timeBase },
      method: 'post',
      data: {
        showContracted: isShowContractedTrue ? 'true' : showContracted,
      },
    },
  );

  const consumptionHelp = useMemo(() => {
    const isVirtual =
      (isNotChildOfRoot && isEndCustomer) ||
      (!isNotChildOfRoot && showContracted !== 'true');

    return t(
      `inventory:table.consumptionTotal${
        isVirtual && isMot ? 'Virtual' : 'Real'
      }Help`,
    );
  }, [isEndCustomer, isMot, isNotChildOfRoot, showContracted, t]);

  const columns = useMemo(
    () =>
      [
        {
          title: t('inventory:table.caller-id'),
          dataIndex: 'msisdn',
          width: `${t('inventory:table.caller-id').length + 6}ch`,
        },
        {
          title: t('inventory:table.iccid'),
          dataIndex: 'iccid',
        },
        {
          title: t('inventory:table.imei'),
          dataIndex: 'imei',
        },
        {
          title: t('inventory:table.hostname'),
          dataIndex: 'hostname',
        },
        ...(isRoot || (isMot && showContracted === 'false')
          ? [
              {
                title: t(
                  `inventory:table.${isMotBusiness ? 'department' : 'client'}`,
                ),
                dataIndex: 'customerName',
              },
            ]
          : []),
        {
          title: t('inventory:table.network-status'),
          dataIndex: 'online',
          align: 'center' as const,
          render: (value: string) => {
            const statusColor = value
              ? theme?.colors?.green
              : theme?.colors?.red;
            return (
              <div
                style={{
                  color: statusColor,
                  display: 'inline-flex',
                  alignItems: 'center',
                  fontWeight: 'bold',
                }}
              >
                <Badge color={statusColor} style={{ marginRight: 8 }} />
                {value ? 'Online' : 'Offline'}
              </div>
            );
          },
        },
        {
          title: (
            <>
              {t('inventory:table.consumptionCurrentDay')}
              <Tooltip title={t('inventory:table.consumptionCurrentDayHelp')}>
                <Button
                  type="link"
                  icon={<QuestionCircleOutlined style={{ color: '#fff' }} />}
                />
              </Tooltip>
            </>
          ),
          width: 180,
          dataIndex: 'consumptionCurrentDay',
          render: (value: string) => bytesFormat(value),
        },
        {
          title: (
            <>
              {t('inventory:table.consumptionTotal')}
              <Tooltip title={consumptionHelp}>
                <Button
                  type="link"
                  icon={<QuestionCircleOutlined style={{ color: '#fff' }} />}
                />
              </Tooltip>
            </>
          ),
          width: 180,
          dataIndex: 'consumptionTotal',
          render: (value: string) => bytesFormat(value),
        },
        ...((isRoot && !isMot) || (!isRoot && !isMotPay && !isMotEssential)
          ? [
              {
                title: t('inventory:table.franchise'),
                dataIndex: 'franchise',
                render: (value: number) => (value ? bytesFormat(value) : null),
              },
            ]
          : []),
        {
          title: t('inventory:table.blocking-status'),
          dataIndex: 'networkBlockStatus',
          align: 'center' as const,
          render: (value: string, record: any) => (
            <BlockStatusTag
              blockType={value}
              blockingInitialDate={record.networkBlockStartDate}
              blockingFinalDate={record.networkBlockEndDate}
            />
          ),
        },
      ].filter(col => col && hasPermission(col.dataIndex, 'inventory')),
    [
      t,
      isRoot,
      isMot,
      showContracted,
      isMotBusiness,
      isMotPay,
      isMotEssential,
      theme?.colors?.green,
      theme?.colors?.red,
      hasPermission,
      consumptionHelp,
    ],
  );

  const dataToReport = useMemo(
    () =>
      !error
        ? data?.map((item: any, index: number) => ({
            key: index,
            msisdn: item.msisdn,
            iccid: item.iccid ? `'${item.iccid}` : '',
            imei: item.imei,
            hostname: item.hostname,
            customerName: item.customerName,
            online: t(`status:${item.online ? 'Online' : 'Offline'}`),
            consumptionCurrentDay: bytesFormat(item.consumptionCurrentDay),
            consumptionTotal: bytesFormat(item.consumptionTotal),
            networkBlockStatus: t(
              `inventory:lock-status.types.${String(item.networkBlockStatus)}`,
            ).toUpperCase(),
            networkBlockStartDate:
              item.networkBlockStartDate &&
              getDateToBRPattern(item.networkBlockStartDate),
            franchise: bytesFormat(item.franchise),
            networkBlockEndDate:
              item.networkBlockEndDate &&
              (item.networkBlockStatus === '3' ||
                item.networkBlockStatus === 'TEMPORARY' ||
                item.networkBlockStatus === 'SUSPENSION' ||
                item.networkBlockStatus === 'UNTIL_RENEWAL')
                ? getDateToBRPattern(item.networkBlockEndDate)
                : '',
          }))
        : [],
    [data, error, t],
  );

  const columnsToReport = useMemo(
    () => [
      ...columns.map(col => {
        let { title } = col;
        if (
          ['consumptionCurrentDay', 'consumptionTotal'].includes(col.dataIndex)
        ) {
          title = t(`inventory:table.${col.dataIndex}`);
        }
        return {
          title,
          dataIndex: col.dataIndex,
          key: col.dataIndex,
        };
      }),
      {
        title: t('inventory:table.networkBlockStartDate'),
        dataIndex: 'networkBlockStartDate',
        key: 'networkBlockStartDate',
      },
      {
        title: t('inventory:table.networkBlockEndDate'),
        dataIndex: 'networkBlockEndDate',
        key: 'networkBlockEndDate',
      },
    ],
    [columns, t],
  );

  const extraActions = useMemo(
    () => (
      <>
        <Radio.Group
          optionType="button"
          buttonStyle="solid"
          onChange={e => setLimit(e.target.value)}
          defaultValue="10"
          value={limit}
          style={{ marginRight: 16 }}
        >
          <Radio.Button value="10">10</Radio.Button>
          <Radio.Button value="20">20</Radio.Button>
          <Radio.Button value="50">50</Radio.Button>
        </Radio.Group>
        <RoleChecker role={rolesConstants.NETWORK_BLOCK_UNLOCK}>
          <Tooltip title={t('top-consumers.blockUnblock')}>
            <Button
              type="default"
              icon={<DisconnectOutlined />}
              onClick={() => setModalDeviceActionsVisible(true)}
            />
          </Tooltip>
        </RoleChecker>
        <Tooltip title={t('top-consumers.showInInventory')}>
          <Button
            type="default"
            icon={<ExpandAltOutlined />}
            onClick={() => {
              const msisdn = data?.map((item: any) => item.msisdn).join(',');
              const page = `/${selectedApp}/${
                hasMotLorawan ? 'mobile-network/' : ''
              }assets/inventory`;
              navigate(`${page}?msisdn=${msisdn}`, { replace: false });
            }}
            disabled={dataToReport?.length === 0 || error}
            style={{ marginLeft: 8 }}
          />
        </Tooltip>

        <CsvExport
          data={dataToReport}
          filename="top-consumers.csv"
          columns={columnsToReport}
          separator=","
        >
          <Tooltip title={t('reports:request.top-consumers-report')}>
            <Button
              icon={<CopyOutlined />}
              disabled={dataToReport?.length === 0 || error}
            />
          </Tooltip>
        </CsvExport>
      </>
    ),
    [
      columnsToReport,
      data,
      dataToReport,
      error,
      hasMotLorawan,
      limit,
      navigate,
      selectedApp,
      t,
    ],
  );

  return (
    <div>
      <ConsumptionAnalysisReport />

      <ConsumptionContainer>
        {!isRoot && !isEndCustomer && (isMotEnterprise || isMotBusiness) && (
          <Tabs
            type="card"
            activeKey={showContracted}
            onChange={key => setShowContracted(key)}
            className="top-consumers-tabs"
          >
            <Tabs.TabPane
              key="false"
              tab={t(
                `inventory:visions-steps.${
                  isMotBusiness ? 'allocated' : 'receive'
                }`,
              )}
            />
            <Tabs.TabPane key="true" tab={t('inventory:visions-steps.pay')} />
          </Tabs>
        )}
        <Table
          noPagination
          title={
            <>
              {t('top-consumers.title')}
              <Radio.Group
                optionType="button"
                buttonStyle="solid"
                onChange={e => setTimeBase(e.target.value)}
                defaultValue="DAY"
                value={timeBase}
                style={{ fontWeight: 400, marginLeft: 8 }}
              >
                <Radio.Button value="DAY">
                  {t('top-consumers.day')}
                </Radio.Button>
                <Radio.Button value="CYCLE">
                  {t('top-consumers.cycle')}
                </Radio.Button>
              </Radio.Group>
            </>
          }
          selectable
          setSelectedRowKeys={(
            keys: any,
            rows: React.SetStateAction<IDevice[] | undefined>,
          ) => {
            setSelectedRows(rows);
          }}
          extraActions={extraActions}
          columns={columns}
          data={data}
          loading={!data && !error}
          tableKey="topConsumers"
        />
        <RoleChecker role={rolesConstants.NETWORK_BLOCK_UNLOCK}>
          <DeviceActionModal
            visible={modalDeviceActionsVisible}
            actionKey={ActionTypes.NETWORK_BLOCK}
            devicesProps={selectedRows}
            onClose={() => setModalDeviceActionsVisible(false)}
          />
        </RoleChecker>
      </ConsumptionContainer>
      <Row style={{ marginTop: 16 }}>
        {showContractDash && (
          <Col span={24}>
            <SharedContracts />
          </Col>
        )}
      </Row>
      {showConsumptionAlerts && (
        <RoleChecker role={rolesConstants.VIEW_CONSUMPTION_ALERTS}>
          <ConsumptionAlerts />
        </RoleChecker>
      )}
    </div>
  );
};

export default Consumption;
